import { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "../../../kit";
import { useViewer } from "../../../../state/ViewerProvider";
import type { VariationCategoryState, VariationConfiguratorState } from "../../../../shared/api-contract";

/**
 * Shared rendering for the v32 Variation Configurator UI — used by both the
 * editor-side control (`VariationConfiguratorControl`) and the customer-facing
 * grid panel (`VariationsGridPanel`). Mirrors webgi's
 * `VariationConfiguratorGridUiPlugin`: each category becomes a titled grid of
 * clickable tiles (swatch/thumbnail), the active item highlighted, click →
 * `applyVariation(category, index)`.
 */

/** How a per-item `icon` string is displayed on a tile. */
export function classifyIcon(icon: string | undefined): { kind: "image" | "color" | "none"; value: string } {
  if (!icon) return { kind: "none", value: "" };
  const s = icon.trim();
  // Explicit hex / css color.
  if (/^#([0-9a-f]{3,8})$/i.test(s) || /^(rgb|hsl)a?\(/i.test(s)) return { kind: "color", value: s };
  if (/^color:/i.test(s)) return { kind: "color", value: s.slice(6).trim() };
  // A URL / data-URI / file path → thumbnail.
  if (/^(https?:|data:|blob:|\/|\.)/i.test(s) || /\.(png|jpe?g|webp|gif|svg|avif)$/i.test(s)) return { kind: "image", value: s };
  return { kind: "none", value: "" };
}

function itemLabel(cat: VariationCategoryState, i: number): string {
  return cat.titles[i]?.trim() || cat.items[i] || `Option ${i + 1}`;
}

export function VariationTile({
  cat,
  index,
  size,
  applying,
  onApply,
}: {
  cat: VariationCategoryState;
  index: number;
  size: number;
  applying: boolean;
  onApply: (index: number) => void;
}) {
  const selected = cat.selected === index;
  const label = itemLabel(cat, index);
  const icon = classifyIcon(cat.icons[index]);
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={selected}
      onClick={() => onApply(index)}
      style={{ width: size, height: size }}
      className={`relative shrink-0 overflow-hidden rounded-lg border transition-all ${
        selected ? "border-ed-accent ring-2 ring-ed-accent/40" : "border-ed-border hover:border-ed-accent"
      }`}
    >
      {icon.kind === "image" ? (
        <img src={icon.value} alt={label} className="h-full w-full object-cover" draggable={false} />
      ) : icon.kind === "color" ? (
        <span className="block h-full w-full" style={{ background: icon.value }} />
      ) : (
        <span className="flex h-full w-full items-center justify-center bg-ed-row">
          <Icon name={cat.type === "materials" ? "palette" : "deployed_code"} size={Math.round(size * 0.42)} className="text-ed-text-3" />
        </span>
      )}
      {selected ? (
        <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-ed-accent text-white shadow">
          <Icon name="check" size={11} />
        </span>
      ) : null}
      {applying ? (
        <span className="absolute inset-0 flex items-center justify-center bg-black/25">
          <Icon name="progress_activity" size={Math.round(size * 0.4)} className="animate-spin text-white" />
        </span>
      ) : null}
    </button>
  );
}

/** A single category: title + a wrapping grid of tiles. */
export function VariationCategory({
  cat,
  tileSize = 44,
  applyingItem,
  showTitle = true,
  showLabels = false,
  onApply,
}: {
  cat: VariationCategoryState;
  tileSize?: number;
  /** index currently being applied (progress "start"), or null. */
  applyingItem: number | null;
  showTitle?: boolean;
  showLabels?: boolean;
  onApply: (index: number) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {showTitle ? (
        <div className="flex items-center justify-between">
          <span className="truncate text-[12px] font-semibold text-ed-text-2">{cat.title || cat.name}</span>
          <span className="shrink-0 text-[12px] text-ed-text-3">{cat.type}</span>
        </div>
      ) : null}
      {cat.items.length === 0 ? (
        <span className="text-[11px] text-ed-text-3">No options.</span>
      ) : showLabels ? (
        <div className="flex flex-col gap-1.5">
          {cat.items.map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <VariationTile cat={cat} index={i} size={tileSize} applying={applyingItem === i} onApply={onApply} />
              <span className={`truncate text-[12px] ${cat.selected === i ? "font-medium text-ed-text" : "text-ed-text-2"}`}>
                {itemLabel(cat, i)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {cat.items.map((_, i) => (
            <VariationTile key={i} cat={cat} index={i} size={tileSize} applying={applyingItem === i} onApply={onApply} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Bookkeeping key for a per-category "currently applying item index" map. */
export type ApplyingMap = Record<string, number | null>;

/**
 * Live Variation Configurator state, shared by the editor control and the
 * customer grid. Seeds from `getVariationConfiguratorState`, keeps a
 * per-category "applying" index driven by the `variationProgress` event, and
 * exposes the mutating RPCs. RPC returns are the source of truth (the store
 * mirrors nothing locally except transient progress).
 */
export function useVariations() {
  const { client } = useViewer();
  const r = client.ring;
  const [state, setState] = useState<VariationConfiguratorState | null>(null);
  const [applying, setApplying] = useState<ApplyingMap>({});
  const [busy, setBusy] = useState(false);
  // Guard against out-of-order async responses clobbering fresher state.
  const seq = useRef(0);

  const commit = useCallback((s: VariationConfiguratorState | null, token: number) => {
    if (token === seq.current) setState(s);
  }, []);

  const reload = useCallback(() => {
    const token = ++seq.current;
    void Promise.resolve(r.getVariationConfiguratorState())
      .then((s) => commit(s, token))
      .catch(() => commit(null, token));
  }, [r, commit]);

  useEffect(() => { reload(); }, [reload]);

  // Progress → per-category applying-index. "start" sets it, terminal phases
  // clear it (index -1 progress rows are the internal loaded/apply steps).
  useEffect(() => {
    return client.on("variationProgress", (p) => {
      setApplying((prev) => {
        if (p.phase === "start") return { ...prev, [p.category]: p.index };
        if (p.phase === "applied" || p.phase === "error") {
          if (prev[p.category] == null) return prev;
          const next = { ...prev };
          delete next[p.category];
          return next;
        }
        return prev;
      });
    });
  }, [client]);

  const apply = useCallback((category: string, index: number) => {
    setApplying((prev) => ({ ...prev, [category]: index }));
    const token = ++seq.current;
    void Promise.resolve(r.applyVariation(category, index))
      .then((s) => commit(s, token))
      .catch(() => {})
      .finally(() => setApplying((prev) => {
        if (prev[category] == null) return prev;
        const next = { ...prev };
        delete next[category];
        return next;
      }));
  }, [r, commit]);

  const setEnabled = useCallback((enabled: boolean) => {
    void Promise.resolve(r.setVariationConfiguratorEnabled(enabled)).finally(reload);
  }, [r, reload]);

  const clear = useCallback(() => {
    void Promise.resolve(r.clearVariations()).finally(reload);
  }, [r, reload]);

  const loadConfigUrl = useCallback((url: string) => {
    setBusy(true);
    const token = ++seq.current;
    void Promise.resolve(r.loadVariationConfig(url))
      .then((s) => commit(s, token))
      .catch(() => {})
      .finally(() => setBusy(false));
  }, [r, commit]);

  const setConfig = useCallback((config: string) => {
    setBusy(true);
    const token = ++seq.current;
    void Promise.resolve(r.setVariationConfig(config))
      .then((s) => commit(s, token))
      .catch(() => {})
      .finally(() => setBusy(false));
  }, [r, commit]);

  return { state, applying, busy, reload, apply, setEnabled, clear, loadConfigUrl, setConfig };
}

